const AUD_MONEY = {
  style: 'currency',
  currency: 'AUD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}

/** Format AUD amounts already stored in dollars (e.g. session turnover, slot analysis). */
export function formatGamingMoney(value) {
  if (value == null || value === '') return '—'
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  return new Intl.NumberFormat('en-AU', AUD_MONEY).format(n)
}

/** Format raw meter counter units (cents) as AUD with decimal places. */
export function formatMeterCents(value) {
  if (value == null || value === '') return '—'
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  return new Intl.NumberFormat('en-AU', AUD_MONEY).format(n / 100)
}

/** Numeric meter counter units → dollars (for charts/exports). */
export function meterCentsToDollars(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return null
  return n / 100
}

const BILL_FACE = [
  { key: 'bill100', face: 100, labels: ['Bill100_alloc', 'bill100_alloc'] },
  { key: 'bill50', face: 50, labels: ['Bill50_alloc', 'bill50_alloc'] },
  { key: 'bill20', face: 20, labels: ['Bill20_alloc', 'bill20_alloc'] },
  { key: 'bill10', face: 10, labels: ['Bill10_alloc', 'bill10_alloc'] },
]

function billCount(row, labels) {
  for (const k of labels) {
    const n = Number(row?.[k])
    if (Number.isFinite(n) && n > 0) return Math.round(n)
  }
  return 0
}

/** Human note mix from allocated bill counts, e.g. "3×$100 + 1×$50". */
export function formatBillBreakdown(row) {
  if (!row || typeof row !== 'object') return ''
  const parts = BILL_FACE
    .map(({ face, labels }) => {
      const n = billCount(row, labels)
      return n > 0 ? `${n}×$${face}` : null
    })
    .filter(Boolean)
  return parts.join(' + ')
}

function isVirtualUncardedRow(row) {
  if (!row || typeof row !== 'object') return false
  const tag = String(row.reconciliation_tag ?? row.Reconciliation_tag ?? '')
  const member = String(row.MemberID ?? row.memberid ?? '')
  const pk = String(row.SessionPK ?? row.sessionpk ?? '')
  return tag === 'virtual_uncarded' || member === '__UNCARDED__' || pk.startsWith('UNC_')
}

/** True when cash-in was overlap-prorated and/or rounded to note denominations (not a direct meter bill read). */
export function cashInShowCalculatedLabel(row) {
  if (!row || typeof row !== 'object') return false
  const alloc = Number(row.CashIn_alloc ?? row.cashin_alloc)
  const raw = Number(row.CashIn_raw_alloc ?? row.cashin_raw_alloc)
  if (!Number.isFinite(alloc) || alloc <= 0) return false
  if (Number.isFinite(raw)) return Math.abs(raw - alloc) >= 1
  // Legacy rows without raw: virtual uncarded amounts are still derived, not meter bill reads.
  if (isVirtualUncardedRow(row)) return true
  // Synthesized note mix stored on attribution — never a direct meter bill read.
  return Boolean(formatBillBreakdown(row))
}

/** @deprecated use cashInShowCalculatedLabel */
export function cashInIsCalculated(row) {
  return cashInShowCalculatedLabel(row)
}

/** Tooltip explaining calculated cash-in provenance. */
export function cashInCalculatedHint(row, cashCents) {
  if (!cashInShowCalculatedLabel(row)) return null
  const alloc = Number(cashCents ?? row?.CashIn_alloc ?? row?.cashin_alloc)
  const raw = Number(row?.CashIn_raw_alloc ?? row?.cashin_raw_alloc)
  const allocLabel = Number.isFinite(alloc) ? formatMeterCents(alloc) : '—'
  if (Number.isFinite(raw) && Math.abs(raw - alloc) >= 1) {
    return `Calculated: overlap prorated ${formatMeterCents(raw)} → ${allocLabel}. Not a direct meter bill read — RXConnect has total CashIn only on this interval.`
  }
  if (Number.isFinite(raw) && Math.abs(raw - alloc) < 1 && alloc > 0 && isVirtualUncardedRow(row)) {
    return `Calculated: full meter interval cash-in (${allocLabel}) — carded session ended before interval end so money-in was not split by play time.`
  }
  return `Calculated: overlap-prorated cash-in ${allocLabel}. Not a direct meter bill read on this interval.`
}

/** CashIn line for Combined / alerts. Never show synthesized note mix — label derived amounts only. */
export function formatCashInWithBills(row, cashCents) {
  const cents = Number(cashCents)
  const cashLabel = Number.isFinite(cents) ? formatMeterCents(cents) : '—'
  if (cashInShowCalculatedLabel(row)) {
    return `${cashLabel} (Calculated)`
  }
  return cashLabel
}

/** Overlap % for Combined interval rows — virtual residual rows are not comparable to carded time share. */
export function formatCombinedOverlapPct(row) {
  if (isVirtualUncardedRow(row)) {
    return {
      text: '—',
      title: 'Uncarded residual row for this meter interval — not a session time-share % (carded rows show overlap only).',
    }
  }
  const f = Number(row?.overlap_fraction)
  if (!Number.isFinite(f)) return { text: '—', title: 'Session time in this meter interval ÷ interval length' }
  return {
    text: `${(f * 100).toFixed(1)}%`,
    title: 'Share of this meter interval covered by the carded session',
  }
}
